import { useCallback, useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { UserFulfillmentRequest } from 'api';
import { useZoom } from 'context/ZoomContext';
import { useFilterPopover } from 'dashboard409a/hooks/useFilterPopover';
import { useModelFilters } from 'dashboard409a/hooks/useModelFilters';
import { ModelFiltersProps } from './filters.props';
import { modelFiltersStyles } from './ModelFilters.styles';
import { UserAutocomplete } from './UserAutocomplete';
import { ModelView } from '../ModelViewsSelector/ModelViewsSelector.props';

export const ModelFilters = ({
  statusOptions,
  statusOptionsLoading,
  onFiltersChange,
  viewToApply,
  views = [],
  currentViewId,
  onViewChange,
  onSaveView,
  onDeleteView,
  onEditView,
}: ModelFiltersProps) => {
  const { handleOpen, popoverProps } = useFilterPopover({
    popoverId: 'model-filters-popover',
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    minHeight: '120px',
  });

  const { zoomLevel } = useZoom();
  const [viewNameDialogOpen, setViewNameDialogOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const isLoadingViewRef = useRef(false);

  const {
    searchParam,
    searchStatus,
    filterType,
    fromDueDate,
    toDueDate,
    selectedPreparers,
    selectedReviewers,
    preparerSearchInput,
    reviewerSearchInput,
    sortedPreparers,
    sortedReviewers,
    isLoadingPreparers,
    isLoadingReviewers,
    handleSearchParamChange,
    handleSearchStatusChange,
    handleFilterTypeChange,
    handleFromDueDateChange,
    handleToDueDateChange,
    handlePreparersChange,
    handleReviewersChange,
    setPreparerSearchInput,
    setReviewerSearchInput,
    cleanFilters,
  } = useModelFilters({
    onFiltersChange,
    viewToApply: viewToApply
      ? {
        preparerIds: viewToApply.preparerIds,
        reviewerIds: viewToApply.reviewerIds,
        searchStatus: viewToApply.searchStatus,
        fromDate: viewToApply.fromDate,
        toDate: viewToApply.toDate,
        filterType: viewToApply.filterType,
      }
      : null,
  });

  const currentView = views.find(v => v.id === currentViewId) || null;
  const isDefaultView = currentViewId === 'default' || !currentViewId;

  // Track when viewToApply changes to prevent auto-save during view loading
  useEffect(() => {
    if (viewToApply) {
      isLoadingViewRef.current = true;
      // Reset the flag after a short delay to allow filters to settle
      const timer = setTimeout(() => {
        isLoadingViewRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewToApply?.id]);

  // Auto-save filters to current view when they change
  useEffect(() => {
    // Don't auto-save if we're currently loading a view
    if (isLoadingViewRef.current) {
      return;
    }

    if (currentViewId && onEditView && currentView) {
      const preparerIds = selectedPreparers.map(p => p.id).filter((id): id is number => id !== undefined);
      const reviewerIds = selectedReviewers.map(r => r.id).filter((id): id is number => id !== undefined);

      const updatedView: ModelView = {
        ...currentView,
        preparerIds,
        reviewerIds,
        searchStatus,
        fromDate: fromDueDate,
        toDate: toDueDate,
        filterType,
      };

      // Only update if something actually changed
      // Sort arrays for comparison (numeric sort for IDs)
      const sortedCurrentPreparerIds = [...currentView.preparerIds].sort((a, b) => a - b);
      const sortedCurrentReviewerIds = [...currentView.reviewerIds].sort((a, b) => a - b);
      const sortedPreparerIds = [...preparerIds].sort((a, b) => a - b);
      const sortedReviewerIds = [...reviewerIds].sort((a, b) => a - b);

      const hasChanged =
        JSON.stringify(sortedCurrentPreparerIds) !== JSON.stringify(sortedPreparerIds) ||
        JSON.stringify(sortedCurrentReviewerIds) !== JSON.stringify(sortedReviewerIds) ||
        currentView.searchStatus !== searchStatus ||
        currentView.fromDate?.getTime() !== fromDueDate?.getTime() ||
        currentView.toDate?.getTime() !== toDueDate?.getTime() ||
        (currentView.filterType || 'current') !== filterType;

      if (hasChanged) {
        onEditView(updatedView);
      }
    }
  }, [
    selectedPreparers,
    selectedReviewers,
    searchStatus,
    fromDueDate,
    toDueDate,
    filterType,
    currentViewId,
    currentView,
    onEditView,
  ]);

  const handleEditCurrentView = useCallback(() => {
    if (currentView && currentView.id !== 'default') {
      setViewName(currentView.name);
      setEditingViewId(currentView.id);
      setViewNameDialogOpen(true);
    }
  }, [currentView]);

  const handleDeleteCurrentView = useCallback(() => {
    if (currentViewId && currentViewId !== 'default' && onDeleteView) {
      onDeleteView(currentViewId);
      if (onViewChange) {
        onViewChange(null);
      }
    }
  }, [currentViewId, onDeleteView, onViewChange]);

  const handleCreateNewView = useCallback(() => {
    setViewName('');
    setEditingViewId(null);
    setViewNameDialogOpen(true);
  }, []);

  const handleSaveViewName = useCallback(() => {
    if (viewName.trim()) {
      if (editingViewId && onEditView) {
        const viewToEdit = views.find(v => v.id === editingViewId);
        if (viewToEdit) {
          const preparerIdsArray = selectedPreparers.map(p => p.id).filter((id): id is number => id !== undefined);
          const reviewerIdsArray = selectedReviewers.map(r => r.id).filter((id): id is number => id !== undefined);
          const updatedView: ModelView = {
            ...viewToEdit,
            name: viewName.trim(),
            preparerIds: preparerIdsArray,
            reviewerIds: reviewerIdsArray,
            searchStatus,
            fromDate: fromDueDate,
            toDate: toDueDate,
            filterType,
          };
          onEditView(updatedView);
        }
      } else if (onSaveView) {
        // onSaveView will create the view with current filters
        onSaveView(viewName.trim());
      }
      setViewNameDialogOpen(false);
      setViewName('');
      setEditingViewId(null);
    }
  }, [
    viewName,
    editingViewId,
    onSaveView,
    onEditView,
    views,
    selectedPreparers,
    selectedReviewers,
    searchStatus,
    fromDueDate,
    toDueDate,
    filterType,
  ]);

  const handleClearFilters = useCallback(() => {
    cleanFilters();
    // Also clear the current view if it's not default
    if (currentViewId && currentViewId !== 'default' && onEditView && currentView) {
      const clearedView: ModelView = {
        ...currentView,
        preparerIds: [],
        reviewerIds: [],
        searchStatus: 'default',
        fromDate: null,
        toDate: null,
        filterType: 'current',
      };
      onEditView(clearedView);
    }
  }, [cleanFilters, currentViewId, currentView, onEditView]);

  const handleViewSelect = useCallback(
    (viewId: string | null) => {
      if (onViewChange) {
        if (viewId === 'default' || !viewId) {
          onViewChange(null);
        } else {
          const selectedView = views.find(v => v.id === viewId);
          if (selectedView) {
            onViewChange(selectedView);
          }
        }
      }
    },
    [onViewChange, views]
  );

  return (
    <>
      <Tooltip title="Filters" arrow>
        <IconButton onClick={handleOpen}>
          <FilterAltIcon />
        </IconButton>
      </Tooltip>
      <Popover {...popoverProps}>
        <Box sx={{ p: 3, minWidth: 800 }}>
          {/* Header with Filter View dropdown */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight="medium">
                Filter View:
              </Typography>
              <Select
                size="small"
                value={currentViewId || 'default'}
                onChange={e => handleViewSelect(e.target.value)}
                sx={{ minWidth: 200 }}
                renderValue={value => {
                  if (value === 'default') {
                    return 'Default';
                  }
                  const view = views.find(v => v.id === value);
                  return view?.name || 'Default';
                }}>
                <MenuItem value="default">Default</MenuItem>
                {views
                  .filter(view => view.id !== 'default')
                  .map(view => (
                    <MenuItem key={view.id} value={view.id}>
                      {view.name}
                    </MenuItem>
                  ))}
              </Select>
              <Tooltip title="Edit View Name">
                <IconButton size="small" onClick={handleEditCurrentView} disabled={!currentView || isDefaultView}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete View">
                <IconButton size="small" onClick={handleDeleteCurrentView} disabled={isDefaultView}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create New View">
                <IconButton size="small" onClick={handleCreateNewView}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <IconButton size="small" onClick={() => popoverProps.onClose?.({}, 'backdropClick')}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* 3 Column Layout */}
          <Grid container spacing={3} mb={3}>
            {/* Column 1: Search, Filter Type, and Select Status */}
            <Grid item xs={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  size="small"
                  label="Search"
                  variant="outlined"
                  value={searchParam}
                  onChange={handleSearchParamChange}
                  fullWidth
                />
                <Select
                  size="small"
                  label="Filter Type"
                  variant="outlined"
                  value={filterType}
                  onChange={handleFilterTypeChange}
                  fullWidth>
                  <MenuItem value={UserFulfillmentRequest.status_filter.CURRENT}>Current</MenuItem>
                  <MenuItem value={UserFulfillmentRequest.status_filter.PAST}>Past</MenuItem>
                  <MenuItem value={UserFulfillmentRequest.status_filter.UNASSIGNED}>Unassigned</MenuItem>
                  <MenuItem value={UserFulfillmentRequest.status_filter.ALL}>All</MenuItem>
                </Select>
                <Select
                  size="small"
                  label="Status"
                  variant="outlined"
                  value={searchStatus}
                  onChange={handleSearchStatusChange}
                  fullWidth>
                  <MenuItem disabled value="default">
                    Select Status
                  </MenuItem>
                  <MenuItem value="All">All</MenuItem>
                  {statusOptionsLoading ? (
                    <MenuItem disabled>Loading...</MenuItem>
                  ) : (
                    statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </Box>
            </Grid>

            {/* Column 2: From and To */}
            <Grid item xs={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <DatePicker
                  label="From"
                  value={fromDueDate}
                  onChange={handleFromDueDateChange}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="To"
                  value={toDueDate}
                  onChange={handleToDueDateChange}
                  minDate={fromDueDate || undefined}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Column 3: Select Reviewers and Select Preparers */}
            <Grid item xs={4}>
              <Box display="flex" flexDirection="column" gap={2}>
                <UserAutocomplete
                  options={sortedReviewers}
                  value={selectedReviewers}
                  onChange={handleReviewersChange}
                  onInputChange={(_event, newInputValue) => {
                    setReviewerSearchInput(newInputValue);
                  }}
                  inputValue={reviewerSearchInput}
                  loading={isLoadingReviewers}
                  label="Select Reviewers"
                  placeholder="Search for reviewers..."
                  zoomLevel={zoomLevel}
                  textFieldSx={modelFiltersStyles.autocompleteTextField}
                />
                <UserAutocomplete
                  options={sortedPreparers}
                  value={selectedPreparers}
                  onChange={handlePreparersChange}
                  onInputChange={(_event, newInputValue) => {
                    setPreparerSearchInput(newInputValue);
                  }}
                  inputValue={preparerSearchInput}
                  loading={isLoadingPreparers}
                  label="Select Preparers"
                  placeholder="Search for preparers..."
                  zoomLevel={zoomLevel}
                  textFieldSx={modelFiltersStyles.preparersTextField}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Footer with Clear Filters button */}
          <Box display="flex" justifyContent="flex-end" mt={3} pt={2} borderTop="1px solid #e0e0e0">
            <Button variant="outlined" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Dialog for saving/editing view name */}
      <Dialog
        open={viewNameDialogOpen}
        onClose={() => {
          setViewNameDialogOpen(false);
          setViewName('');
          setEditingViewId(null);
        }}>
        <DialogTitle>{editingViewId ? 'Edit View Name' : 'Create New View'}</DialogTitle>
        <DialogContent>
          <TextField
            label="View Name"
            value={viewName}
            onChange={e => setViewName(e.target.value)}
            fullWidth
            autoFocus
            margin="normal"
            onKeyDown={e => {
              if (e.key === 'Enter' && viewName.trim()) {
                e.preventDefault();
                handleSaveViewName();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewNameDialogOpen(false);
              setViewName('');
              setEditingViewId(null);
            }}>
            Cancel
          </Button>
          <Button onClick={handleSaveViewName} variant="contained" disabled={!viewName.trim()}>
            {editingViewId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
